import React from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge, Button, Table } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import type { ProductFeeRecord, RefLabel } from '../services/productFees';

interface Props {
    data: ProductFeeRecord[];
    onEdit: (row: ProductFeeRecord) => void;
    onDelete: (id: string) => void;
    onOverridePayerPair?: (row: ProductFeeRecord) => void;
    onClearPayerPairOverride?: (row: ProductFeeRecord) => void;
    isLoading?: boolean;
    activeQuoteTypes: RefLabel[];
    disallowedSchedulingIds?: ReadonlySet<string>;
}

function formatMoney(m: { amount: string; currency: string } | null | undefined): string {
    if (!m) return '—';
    return `£${m.amount}`;
}

export const ProductFeeDataTable: React.FC<Props> = ({
    data, onEdit, onDelete, onOverridePayerPair, onClearPayerPairOverride, isLoading, activeQuoteTypes, disallowedSchedulingIds,
}) => {
    const columns: ColumnDef<ProductFeeRecord>[] = [
        {
            id: 'fee',
            header: 'Fee',
            enableSorting: false,
            cell: (info) => (
                <div>
                    <span className="font-semibold text-sm">{info.row.original.fee.name}</span>
                    {info.row.original.fee.transaction_type && (
                        <Badge size="xs" variant="info">
                            {info.row.original.fee.transaction_type.code}
                        </Badge>
                    )}
                </div>
            ),
        },
        {
            id: 'value_type',
            header: 'Value Type',
            enableSorting: false,
            cell: (info) => (
                <span className="text-sm">{info.row.original.value_type?.name ?? '—'}</span>
            ),
        },
        {
            id: 'paid_by_to',
            header: 'Paid By / To',
            enableSorting: false,
            cell: (info) => (
                <span className="text-sm">
                    {info.row.original.paid_by?.name ?? '—'} &rarr; {info.row.original.paid_to?.name ?? '—'}
                    {info.row.original.payer_pair_overridden && (
                        <Badge size="xs" variant="warning">Override</Badge>
                    )}
                </span>
            ),
        },
        {
            id: 'scheduling',
            header: 'Scheduling',
            enableSorting: false,
            cell: (info) => (
                <span className="text-sm">
                    {info.row.original.scheduling_method?.name ?? '—'}
                    {info.row.original.scheduling_method && disallowedSchedulingIds?.has(info.row.original.scheduling_method.id) && (
                        <Badge size="xs" variant="warning">Not allowed for this product type</Badge>
                    )}
                </span>
            ),
        },
        {
            id: 'applies_to',
            header: 'Applies To',
            enableSorting: false,
            cell: (info) => {
                const appliesTo = info.row.original.applies_to ?? [];
                const isAll = activeQuoteTypes.length > 0
                    && activeQuoteTypes.every((q) => appliesTo.some((a) => a.id === q.id));
                return (
                    <span className="text-sm">
                        {isAll ? 'All' : (appliesTo.map((a) => a.name).join(', ') || '—')}
                    </span>
                );
            },
        },
        {
            id: 'vat_category',
            header: 'VAT',
            enableSorting: false,
            cell: (info) => (
                <span className="text-sm">{info.row.original.vat_category?.name ?? '—'}</span>
            ),
        },
        {
            id: 'scope',
            header: 'Scope',
            enableSorting: false,
            cell: (info) => (
                <span className="text-sm">
                    {info.row.original.application_scope === 'per_plan' ? 'Per Plan' : 'Per Service'}
                </span>
            ),
        },
        {
            id: 'absorbed',
            header: 'Absorbed',
            enableSorting: false,
            cell: (info) => (
                info.row.original.is_absorbed
                    ? <Badge size="xs" variant="success">Yes</Badge>
                    : <span className="text-sm text-mp-muted">No</span>
            ),
        },
        {
            id: 'default',
            header: 'Default Value',
            enableSorting: false,
            cell: (info) => {
                const row = info.row.original;
                if (row.default_percentage !== null && row.default_percentage !== undefined) {
                    return <span className="text-sm font-mono">{row.default_percentage}%</span>;
                }
                if (row.default_cost_net_money) {
                    return <span className="text-sm font-mono">{formatMoney(row.default_cost_net_money)} net</span>;
                }
                if (row.default_cost_gross_money) {
                    return <span className="text-sm font-mono">{formatMoney(row.default_cost_gross_money)} gross</span>;
                }
                return <span className="text-sm text-mp-muted">—</span>;
            },
        },
        {
            id: 'cap',
            header: 'Cap',
            enableSorting: false,
            cell: (info) => (
                <span className="text-sm font-mono">{formatMoney(info.row.original.cap_value_gross_money)}</span>
            ),
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => (
                <div className="flex gap-2">
                    <PermissionGuard permission={{ resource: 'smr', action: 'product_fee.edit' }}>
                        <Button size="xs" variant="secondary" onClick={() => onEdit(info.row.original)} disabled={isLoading}>
                            Edit
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission={{ resource: 'smr', action: 'product_fee.override_payer_pair' }}>
                        <div className="flex gap-2">
                            {onOverridePayerPair && (
                                <Button size="xs" variant="outline" onClick={() => onOverridePayerPair(info.row.original)} disabled={isLoading}>
                                    Override payer pair
                                </Button>
                            )}
                            {onClearPayerPairOverride && info.row.original.payer_pair_overridden && (
                                <Button size="xs" variant="outline" onClick={() => onClearPayerPairOverride(info.row.original)} disabled={isLoading}>
                                    Use default pair
                                </Button>
                            )}
                        </div>
                    </PermissionGuard>
                    <PermissionGuard permission={{ resource: 'smr', action: 'product_fee.delete' }}>
                        <Button size="xs" variant="outline" onClick={() => onDelete(info.row.original.id)} disabled={isLoading}>
                            Delete
                        </Button>
                    </PermissionGuard>
                </div>
            ),
        },
    ];

    return <Table<ProductFeeRecord> data={data} columns={columns} enableSorting={false} />;
};
